
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormArray, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractFormDialog, AbstractFormDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';

import { startWith, map } from 'rxjs/operators';

import { OrderplanCropAidDisplayData, ProductInventoryData } from '../../models';

export interface CustomerProductInventoryDialogData extends AbstractFormDialogData {
  orderplanByCropAids: OrderplanCropAidDisplayData[];
}

export interface CustomerProductInventoryDialogResult {
  customerProductInventory: number[];
}

interface IndexedOrderplanCropAidDisplayData extends OrderplanCropAidDisplayData {
  index: number;
}

@Component({
  selector: 'app-customer-product-inventory-dialog',
  templateUrl: './customer-product-inventory-dialog.component.html',
  styleUrls: ['./customer-product-inventory-dialog.component.scss'],
  standalone: false,
})
export class CustomerProductInventoryDialogComponent extends AbstractFormDialog<ProductInventoryData[]> implements OnInit {
  data = inject<CustomerProductInventoryDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef =
    inject<MatDialogRef<CustomerProductInventoryDialogComponent, AbstractDialogResultData<ProductInventoryData[]>>>(MatDialogRef);

  formGroup = this.formBuilder.group({
    customerProductInventory: this.formBuilder.array(
      this.data.orderplanByCropAids.map((orderplanByCropAid) => [orderplanByCropAid.customerProductInventoryAmount, [Validators.required, Validators.min(0)]]),
    ),
  });

  cropAidFilter = new FormControl();
  hasFilter = false;
  filteredCropAids$ = this.cropAidFilter.valueChanges.pipe(
    startWith(''),
    map(
      (search: string) =>
        [search, this.data.orderplanByCropAids.map((cropAid, index) => ({ ...cropAid, index }))] as [string, IndexedOrderplanCropAidDisplayData[]],
    ),
    map(([search, cropAids]) => (search ? cropAids.filter(({ cropAid }) => cropAid.name.toUpperCase().includes(search.toUpperCase())) : cropAids)),
  );
  displayedColumns = ['product', 'requisite', 'inventory', 'ordered', 'result'];
  ngOnInit(): void {
    this.cropAidFilter.valueChanges.subscribe((value) => {
      this.hasFilter = value !== undefined && value !== null && value !== '';
    });
  }

  getCustomerProductInventory(): FormArray {
    return this.formGroup.controls.customerProductInventory as FormArray;
  }

  getProductInventoryControl(i: number): FormControl {
    return this.getCustomerProductInventory().controls[i] as FormControl;
  }

  getProductInventory(i: number): number {
    return this.getProductInventoryControl(i).value as number;
  }

  getTotalUsedAmount(i: number): number {
    const productInventoryAmount = this.getProductInventory(i);
    const orderplanByCropAid = this.data.orderplanByCropAids[i];

    return productInventoryAmount + orderplanByCropAid.orderLinesTotalAmount;
  }

  getShortageAmount(i: number) {
    const totalUsedAmount = this.getTotalUsedAmount(i);
    const shortage = this.data.orderplanByCropAids[i].requisiteAmount - totalUsedAmount;

    return shortage >= 0 ? shortage : 0;
  }

  getExcessAmount(i: number) {
    const totalUsedAmount = this.getTotalUsedAmount(i);
    const excessAmount = totalUsedAmount - this.data.orderplanByCropAids[i].requisiteAmount;

    return excessAmount >= 0 ? excessAmount : 0;
  }

  process(data: CustomerProductInventoryDialogResult) {
    const productInventories = data.customerProductInventory.map(
      (productInventory, i): ProductInventoryData => ({
        amount: productInventory,
        cropAidId: this.data.orderplanByCropAids[i].cropAid.id,
      }),
    );

    super.confirm(productInventories);
  }
}
